import type { OperatorActivity } from '@erp/rpc';
import { OperatorAgent } from './operatorAgent';
import type { OperatorSessionStream } from './useOperatorSessionStream';

// ---------------------------------------------------------------------------
// One AG-UI run per session, however many components are watching it.
//
// `useOperatorSessionStream` used to open its own `OperatorAgent` per call, and several pages
// watch one session twice over: the Agent Monitor subscribes for its trajectory rail while the
// `AgentSessionViewer` inside its live panel subscribes again, and the operator's SOP detail page
// does the same. That is two SSE connections and two server-side runs for one desktop.
//
// This keeps one entry per session, ref-counted by its subscribers. The hook's signature is
// unchanged, so the sharing is invisible to every call site.
// ---------------------------------------------------------------------------

const INITIAL: OperatorSessionStream = {
  activity: null,
  finished: false,
  unavailable: false,
  error: null,
};

interface StreamEntry {
  agent: OperatorAgent;
  subscription: { unsubscribe: () => void };
  refCount: number;
  /**
   * Replaced immutably on every update. `getSnapshot` returns THIS object and never builds one:
   * `useSyncExternalStore` re-renders on identity, so constructing a fresh object per read would
   * loop forever.
   */
  snapshot: OperatorSessionStream;
  listeners: Set<() => void>;
  /** Set while a release is pending — see `release`. */
  teardown: ReturnType<typeof setTimeout> | null;
}

export interface OperatorStreamHandle {
  subscribe: (onStoreChange: () => void) => () => void;
  getSnapshot: () => OperatorSessionStream;
  release: () => void;
}

const entries = new Map<string, StreamEntry>();

type AgentFactory = (options: {
  threadId: string;
  sessionId: string;
  organizationId: string;
}) => OperatorAgent;

let createAgent: AgentFactory = (options) => new OperatorAgent(options);

/**
 * The identity of a stream.
 *
 * `threadId` is part of the key on purpose: the Unified Studio runs a capture stream and a build
 * stream against one session deliberately, and they must not collapse into each other.
 */
export function operatorStreamKey(
  organizationId: string,
  sessionId: string,
  threadId?: string,
): string {
  return `${organizationId}|${sessionId}|${threadId ?? sessionId}`;
}

function update(
  entry: StreamEntry,
  patch: (prev: OperatorSessionStream) => OperatorSessionStream,
): void {
  entry.snapshot = patch(entry.snapshot);
  for (const listener of entry.listeners) listener();
}

function open(
  key: string,
  organizationId: string,
  sessionId: string,
  threadId: string | undefined,
): StreamEntry {
  const agent = createAgent({
    threadId: threadId ?? sessionId,
    sessionId,
    organizationId,
  });

  const entry: StreamEntry = {
    agent,
    // Replaced immediately below; the subscriber callbacks need `entry` to exist first.
    subscription: { unsubscribe: () => undefined },
    refCount: 0,
    snapshot: INITIAL,
    listeners: new Set(),
    teardown: null,
  };

  // Fully subscriber-driven (the AG-UI AgentSubscriber pattern).
  entry.subscription = agent.subscribe({
    // The client reduces STATE_SNAPSHOT/STATE_DELTA into agent.state; mirror it into the
    // snapshot. `state` is the operator panel (OperatorActivity).
    onStateChanged: ({ state: agentState }) => {
      update(entry, (prev) => ({
        ...prev,
        activity: agentState as unknown as OperatorActivity,
      }));
    },
    // Server RUN_ERROR (e.g. SESSION_UNAVAILABLE) → callers fall back to the poll.
    onRunErrorEvent: ({ event }) => {
      update(entry, (prev) => ({
        ...prev,
        unavailable: true,
        error: event.code ? `${event.code}: ${event.message}` : event.message,
      }));
    },
    // Transport-level failure (dropped stream) — never arrives as a RUN_ERROR event, so
    // onRunErrorEvent alone would miss it.
    onRunFailed: ({ error }) => {
      update(entry, (prev) => ({
        ...prev,
        unavailable: true,
        error: prev.error ?? error.message,
      }));
    },
    // Run done (success or failure).
    onRunFinalized: () => {
      update(entry, (prev) => ({ ...prev, finished: true }));
    },
  });

  entries.set(key, entry);

  // Lifecycle is handled by the subscriber hooks above; swallow the promise so a rejection
  // doesn't surface as an unhandled rejection.
  void agent.runAgent({ runId: crypto.randomUUID() }).catch(() => undefined);

  return entry;
}

/**
 * Join the stream for a session, opening it if this is the first watcher.
 *
 * A second watcher arriving mid-run gets the current snapshot immediately rather than starting
 * from nothing — AG-UI's STATE_SNAPSHOT makes the state self-contained, so there is nothing to
 * replay.
 */
export function acquireOperatorStream(
  organizationId: string,
  sessionId: string,
  threadId?: string,
): OperatorStreamHandle {
  const key = operatorStreamKey(organizationId, sessionId, threadId);
  const entry =
    entries.get(key) ?? open(key, organizationId, sessionId, threadId);

  entry.refCount += 1;
  if (entry.teardown !== null) {
    clearTimeout(entry.teardown);
    entry.teardown = null;
  }

  let released = false;
  return {
    subscribe: (onStoreChange) => {
      entry.listeners.add(onStoreChange);
      return () => entry.listeners.delete(onStoreChange);
    },
    getSnapshot: () => entry.snapshot,
    release: () => {
      // Guard double-release: React can run a cleanup more than once, and decrementing twice for
      // one acquire would close a stream other components are still watching.
      if (released) return;
      released = true;
      entry.refCount -= 1;
      if (entry.refCount > 0) return;
      // Deferred by a tick rather than closed outright. StrictMode mounts, unmounts and remounts
      // every effect in development, and a navigation between two pages watching the same session
      // unmounts the old one before mounting the new one — closing synchronously would drop the
      // SSE connection and immediately reopen it in both cases.
      entry.teardown = setTimeout(() => {
        entries.delete(key);
        entry.subscription.unsubscribe();
        entry.agent.abortRun();
      }, 0);
    },
  };
}

/** Reset the module state between tests, optionally with a stub agent. */
export function resetOperatorStreamsForTests(factory?: AgentFactory): void {
  for (const entry of entries.values()) {
    if (entry.teardown !== null) clearTimeout(entry.teardown);
    entry.subscription.unsubscribe();
    entry.agent.abortRun();
  }
  entries.clear();
  createAgent = factory ?? ((options) => new OperatorAgent(options));
}

/** How many streams are open. Tests only — the point of this module is that this stays at 1. */
export function openOperatorStreamCountForTests(): number {
  return entries.size;
}
